import {
  FileCheckIcon,
  FilePenIcon,
  FileSearchIcon,
  ScaleIcon,
  type LucideIcon,
} from "lucide-react";

import promptExamplesData from "./promptExamples.json";

export type PromptExampleCategoryId = "practice" | "case" | "drafting" | "review";

export interface PromptExampleCategory {
  id: PromptExampleCategoryId;
  label: string;
  icon: LucideIcon;
}

export interface PromptExample {
  categoryId: PromptExampleCategoryId;
  text: string;
}

const CATEGORY_ICONS: Record<PromptExampleCategoryId, LucideIcon> = {
  practice: ScaleIcon,
  case: FileSearchIcon,
  drafting: FilePenIcon,
  review: FileCheckIcon,
};

function isPromptExampleCategoryId(value: string): value is PromptExampleCategoryId {
  return value in CATEGORY_ICONS;
}

export const PROMPT_EXAMPLE_CATEGORIES: ReadonlyArray<PromptExampleCategory> =
  promptExamplesData.categories.flatMap((category) =>
    isPromptExampleCategoryId(category.id)
      ? [{ id: category.id, label: category.label, icon: CATEGORY_ICONS[category.id] }]
      : [],
  );

export const PROMPT_EXAMPLES: ReadonlyArray<PromptExample> = promptExamplesData.examples.flatMap(
  (example) =>
    isPromptExampleCategoryId(example.categoryId)
      ? [{ categoryId: example.categoryId, text: example.text }]
      : [],
);

export const PROMPT_EXAMPLES_COLLAPSED_COUNT = 4;

export function shufflePromptExamples(
  examples: ReadonlyArray<PromptExample>,
  random: () => number = Math.random,
): PromptExample[] {
  const result = examples.slice();
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    const current = result[index]!;
    result[index] = result[swapIndex]!;
    result[swapIndex] = current;
  }
  return result;
}
